import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, map, catchError, of, timeout } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppEvent } from '../models/event.model';

// ─── Interfaces ───────────────────────────────────────────────

export interface ItinerantKPIs {
  eventsThisMonth: number;
  eventsThisMonthDiff: number;
  eventsTrackedThisMonth: number;
  totalSavings: number;
  totalSavingsTrend: number | null;
  avgNetProfitPerEvent: number | null;
  avgNetProfitPerEventTrend: number | null;
  aiPrecision: number | null;
  aiPrecisionTrend: number | null;
  topProduct: string | null;
  topProductSales: number;
}

export interface WeatherAlert {
  id: string;
  severity: 'info' | 'warning' | 'danger';
  icon: string;
  title: string;
  message: string;
  advice: string;
  eventName?: string;
  date: string;
}

export interface AiInsight {
  id: string;
  type: 'opportunity' | 'pattern' | 'warning' | 'tip';
  icon: string;
  title: string;
  message: string;
  impact?: string;
}

export interface TimelineDay {
  date: Date;
  dayName: string;
  dayNumber: number;
  isToday: boolean;
  weather?: {
    icon: string;
    temperature: number;
    temperatureMin?: number;
    temperatureMax?: number;
    condition?: string;
    weatherCode?: number;
  };
  events: AppEvent[];
  opportunities: Opportunity[];
  specialPeriods?: { label: string; type: string; color?: string | null }[];
  /** @deprecated use specialPeriods */
  specialPeriod?: { label: string; type: string; color?: string | null } | null;
}

export interface Opportunity {
  id: string;
  name: string;
  description?: string;
  location: string;
  fullAddress?: string;
  lat: number;
  lng: number;
  distance: string;
  distanceKm: number;
  score: number;
  // Date et horaires
  date: string;
  dateFormatted: string;
  timeRange: string;
  endDate?: string;
  // Affluence et revenus
  expectedAttendance: number;
  estimatedRevenue: { low: number; high: number };
  estimatedMargin: { low: number; high: number };
  // Indicateurs de viabilité économique
  minViableCA: number;
  isViable: boolean;
  profitabilityLevel: 'high' | 'medium' | 'low'; // high=vert, medium=jaune, low=orange
  potentialCustomers: number;
  expectedCustomers: number;
  capacityUsed: number;
  // Indicateurs métier
  activityMatch: number;
  activityMatchLabel: string;
  competition: string;
  isFree: boolean;
  eventType: string;
  eventTypeLabel: string;
  // Infos organisateur
  source: string;
  url?: string;
  imageUrl?: string;
  organizerContact?: string;
  // Tags
  tags?: string[];
  price?: string;
}

export interface MerchantMapLocation {
  lat: number;
  lng: number;
  address: string;
  city: string;
  actionRadius: number; // km
}

export interface TopEvent {
  id: string;
  name: string;
  date: string;
  revenue: number;
  profit: number;
  estimatedProfit: number | null;
  accuracy: number;
  eventType: string;
}

export interface EventsByStatus {
  planned: AppEvent[];
  active: AppEvent[];
  completed: AppEvent[]; // without sales entry
  analyzed: AppEvent[];
}

// ─── Phase 2: Moteur reco visible ─────────────────────────────

export interface RecoProduct {
  productName: string;
  unit: string;
  recommended: number;
  rangeLow: number;
  rangeHigh: number;
  coefficients: {
    base: number;
    eventType: number;
    weather: number;
    duration: number;
  };
  actualSold?: number;
  actualWasted?: number;
}

export interface PrecisionPoint {
  eventId: string;
  eventName: string;
  date: string;
  precision: number;
}

export interface RecoTransparency {
  eventId: string;
  eventName: string;
  products: RecoProduct[];
}

// ─── Phase 3: Checklist ───────────────────────────────────────

export interface ChecklistItem {
  id: string;
  label: string;
  category:
    | 'approvisionnement'
    | 'logistique'
    | 'meteo'
    | 'administratif'
    | 'conservation';
  completed: boolean;
  contextual?: boolean; // generated from weather/product context
  quantity?: string; // e.g. "120 unités" for approvisionnement items
}

export interface EventChecklist {
  eventId: string;
  eventName: string;
  items: ChecklistItem[];
}

// ─── Service ──────────────────────────────────────────────────

@Injectable({
  providedIn: 'root',
})
export class DashboardItinerantService {
  private apiUrl = `${environment.apiUrl}/dashboard/itinerant`;
  private eventsUrl = `${environment.apiUrl}/events`;

  constructor(private http: HttpClient) {}

  /** KPIs spécifiques itinérant */
  getKPIs(): Observable<ItinerantKPIs> {
    return this.http.get<ItinerantKPIs>(`${this.apiUrl}/kpis`);
  }

  /** Événements groupés par statut */
  getEventsByStatus(): Observable<EventsByStatus> {
    return this.http.get<EventsByStatus>(`${this.apiUrl}/events/by-status`);
  }

  /** Timeline 7 jours (événements + opportunités + météo) */
  getTimeline(): Observable<TimelineDay[]> {
    return this.http.get<TimelineDay[]>(`${this.apiUrl}/timeline`);
  }

  /** Alertes météo pour les prochains événements */
  getWeatherAlerts(): Observable<WeatherAlert[]> {
    return this.http.get<WeatherAlert[]>(`${this.apiUrl}/weather-alerts`);
  }

  /** Insights IA contextuels */
  getInsights(): Observable<AiInsight[]> {
    return this.http.get<AiInsight[]>(`${this.apiUrl}/insights`);
  }

  /** Top événements par CA */
  getTopEvents(limit: number = 3): Observable<TopEvent[]> {
    return this.http.get<TopEvent[]>(`${this.apiUrl}/top-events`, {
      params: { limit: limit.toString() },
    });
  }

  /** Opportunités à proximité */
  getOpportunities(): Observable<Opportunity[]> {
    return this.http.get<Opportunity[]>(`${this.apiUrl}/opportunities`);
  }

  /** Suivre une opportunité (la convertir en événement) */
  followOpportunity(
    opportunity: Opportunity,
  ): Observable<{ success: boolean; message: string; event?: any }> {
    return this.http.post<{ success: boolean; message: string; event?: any }>(
      `${this.apiUrl}/opportunities/${opportunity.id}/follow`,
      opportunity,
    );
  }

  /** Recommandations transparentes pour un événement */
  getRecommendations(eventId: string): Observable<RecoTransparency> {
    return this.http
      .get<any>(`${this.eventsUrl}/${eventId}/recommandation`)
      .pipe(
        map((resp) => ({
          eventId,
          eventName: resp.eventName || '',
          products: resp.recommendations || resp.products || [],
        })),
      );
  }

  /** Historique de précision IA */
  getPrecisionHistory(): Observable<PrecisionPoint[]> {
    return this.http.get<PrecisionPoint[]>(`${this.apiUrl}/precision-history`);
  }

  /** Checklist pré-événement — route non implémentée, retourne vide */
  getChecklist(_eventId: string): Observable<EventChecklist> {
    // TODO: implémenter GET /api/dashboard/itinerant/checklist/:id côté backend
    return of({ eventId: _eventId, eventName: '', items: [] });
  }

  /** Chargement complet du dashboard en un seul appel parallèle */
  loadDashboard(): Observable<{
    kpis: ItinerantKPIs;
    eventsByStatus: EventsByStatus;
    timeline: TimelineDay[];
    weatherAlerts: WeatherAlert[];
    insights: AiInsight[];
    topEvents: TopEvent[];
    opportunities: Opportunity[];
    precisionHistory: PrecisionPoint[];
  }> {
    return forkJoin({
      kpis: this.getKPIs().pipe(
        timeout(10000),
        catchError(() => of(this.emptyKPIs())),
      ),
      eventsByStatus: this.getEventsByStatus().pipe(
        timeout(10000),
        catchError(() =>
          of({ planned: [], active: [], completed: [], analyzed: [] }),
        ),
      ),
      timeline: this.getTimeline().pipe(
        timeout(10000),
        catchError(() => of([])),
      ),
      weatherAlerts: this.getWeatherAlerts().pipe(
        timeout(10000),
        catchError(() => of([])),
      ),
      insights: this.getInsights().pipe(
        timeout(10000),
        catchError(() => of([])),
      ),
      topEvents: this.getTopEvents().pipe(
        timeout(10000),
        catchError(() => of([])),
      ),
      // opportunities peut être lent (sync API externe) → timeout plus généreux
      opportunities: this.getOpportunities().pipe(
        timeout(15000),
        catchError(() => of([])),
      ),
      precisionHistory: this.getPrecisionHistory().pipe(
        timeout(10000),
        catchError(() => of([])),
      ),
    });
  }

  private emptyKPIs(): ItinerantKPIs {
    return {
      eventsThisMonth: 0,
      eventsThisMonthDiff: 0,
      eventsTrackedThisMonth: 0,
      totalSavings: 0,
      totalSavingsTrend: null,
      avgNetProfitPerEvent: null,
      avgNetProfitPerEventTrend: null,
      aiPrecision: null,
      aiPrecisionTrend: null,
      topProduct: null,
      topProductSales: 0,
    };
  }
}
